import { FC, useEffect, useState } from 'react';
import Image from 'next/legacy/image';

import { RedeemBadge } from '.';
import { useToggleFavourites } from 'reactQuery/mutations';
import { ProductTileProps, Styled } from '.';
import { useFetchFavouriteProductIds } from 'reactQuery/queries';
import { AddToCartConfirmationModal } from 'components/AddToCartConfirmationModal';

import {
  filterBulkDiscountBySku,
  filterCouponsBySku,
  filterRedeemProductBySku,
  isRedeemAvailable,
} from 'components/ProductTile/ProductTile.helper';
import DiscountRibbon from 'components/ProductTile/DiscountRibbon';
import { MaterialTypeEnum } from 'types';
import { useRouter } from 'next/router';
import getConfig from 'next/config';
import { useI18n } from 'contexts/i18n';
import { isAPACStore, isZeroCurrency } from 'utils';
import { useLoggedInUserTypeStore } from 'store';
import { useDsoToggleFavourites } from 'reactQuery/mutations/dso/useDsoToggleFavourites';
import { useDsoFetchFavouriteProductIds } from 'reactQuery/queries/dso/useDsoFetchFavouriteProductIds';

const ProductTile: FC<ProductTileProps> = ({
  product,
  isLoading = false,
  isError = false,
  doctorPromotionList,
  dsoPromotionList,
}) => {
  const loggedInUserType = useLoggedInUserTypeStore(state => state.userType);
  const isDso = loggedInUserType === 'dso';
  const router = useRouter();
  const { data: dsoFavouriteProductIds } = useDsoFetchFavouriteProductIds({
    enabled: isDso,
  });
  const { data: favouriteProductIds } = useFetchFavouriteProductIds({
    enabled: !isDso,
  });

  const [bookmark, setBookmark] = useState(false);
  const [showAddToCartConfirmation, setShowAddToCartConfirmation] =
    useState(false);
  const i18n = useI18n();
  const [screenWidth, setScreenWidth] = useState<number>(window.innerWidth);

  const isOrderable =
    product.materialType === MaterialTypeEnum.physical ||
    product.materialType === MaterialTypeEnum.digital_physical;

  function handleWindowSizeChange() {
    setScreenWidth(window.innerWidth);
  }

  const toggleDsoFavouriteMutation = useDsoToggleFavourites();
  const toggleFavouriteMutation = useToggleFavourites();

  const handleToggleFavourites = (productID: string) => {
    const mutationToUse = isDso
      ? toggleDsoFavouriteMutation
      : toggleFavouriteMutation;
    mutationToUse?.mutate({ variantId: productID.toString() });
    setBookmark(!bookmark);
  };

  useEffect(() => {
    const ids = isDso ? dsoFavouriteProductIds : favouriteProductIds;

    if (ids) {
      const isBookmarked = ids.includes(product.variantId);
      setBookmark(isBookmarked);
    }

    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    };
  }, [isDso, dsoFavouriteProductIds, favouriteProductIds, product.variantId]);

  const isRedeemEnabled = isRedeemAvailable(
    product.sku,
    doctorPromotionList?.redeemProductList
  );
  const redeemProduct = filterRedeemProductBySku(
    product.sku,
    doctorPromotionList?.redeemProductList
  );

  const { publicRuntimeConfig } = getConfig();
  const typodontSKUs = publicRuntimeConfig.APAC_TYPODONT_SKUS.split(',');
  const isTypodont = typodontSKUs.includes(product.sku);
  const isDownloadable =
    product.materialType === MaterialTypeEnum.digital_physical ||
    product.materialType === MaterialTypeEnum.digital;

  const couponProduct =
    isZeroCurrency(product.displayPrice) || isDownloadable
      ? undefined
      : filterCouponsBySku(product.sku, doctorPromotionList);
  const bulkDiscountProduct =
    isZeroCurrency(product.displayPrice) || isDownloadable
      ? undefined
      : filterBulkDiscountBySku(product.sku, doctorPromotionList);
  const hasMultipleOffers = Boolean(couponProduct && bulkDiscountProduct);

  if (isLoading || isError) {
    return (
      <Styled.ProductTileLoadingContainer>
        <Styled.LoadingProductImage>
          <Image
            src="data:image/svg+xml;base64,Cjxzdmcgd2lkdGg9IjcwMCIgaGVpZ2h0PSI0NzUiIHZlcnNpb249IjEuMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayI+CiAgPGRlZnM+CiAgICA8bGluZWFyR3JhZGllbnQgaWQ9ImciPgogICAgICA8c3RvcCBzdG9wLWNvbG9yPSIjY2JjYmNiIiBvZmZzZXQ9IjIwJSIgLz4KICAgICAgPHN0b3Agc3RvcC1jb2xvcj0iI2Y5ZjlmOSIgb2Zmc2V0PSI1MCUiIC8+CiAgICAgIDxzdG9wIHN0b3AtY29sb3I9IiNjYmNiY2IiIG9mZnNldD0iNzAlIiAvPgogICAgPC9saW5lYXJHcmFkaWVudD4KICA8L2RlZnM+CiAgPHJlY3Qgd2lkdGg9IjcwMCIgaGVpZ2h0PSI0NzUiIGZpbGw9IiNjYmNiY2IiIC8+CiAgPHJlY3QgaWQ9InIiIHdpZHRoPSI3MDAiIGhlaWdodD0iNDc1IiBmaWxsPSJ1cmwoI2cpIiAvPgogIDxhbmltYXRlIHhsaW5rOmhyZWY9IiNyIiBhdHRyaWJ1dGVOYW1lPSJ4IiBmcm9tPSItNzAwIiB0bz0iNzAwIiBkdXI9IjFzIiByZXBlYXRDb3VudD0iaW5kZWZpbml0ZSIgIC8+Cjwvc3ZnPg=="
            alt="Prod Image"
            layout="fill"
            objectFit="contain"
            sizes="25vw"
            placeholder="blur"
            blurDataURL="data:image/svg+xml;base64,Cjxzdmcgd2lkdGg9IjcwMCIgaGVpZ2h0PSI0NzUiIHZlcnNpb249IjEuMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayI+CiAgPGRlZnM+CiAgICA8bGluZWFyR3JhZGllbnQgaWQ9ImciPgogICAgICA8c3RvcCBzdG9wLWNvbG9yPSIjY2JjYmNiIiBvZmZzZXQ9IjIwJSIgLz4KICAgICAgPHN0b3Agc3RvcC1jb2xvcj0iI2Y5ZjlmOSIgb2Zmc2V0PSI1MCUiIC8+CiAgICAgIDxzdG9wIHN0b3AtY29sb3I9IiNjYmNiY2IiIG9mZnNldD0iNzAlIiAvPgogICAgPC9saW5lYXJHcmFkaWVudD4KICA8L2RlZnM+CiAgPHJlY3Qgd2lkdGg9IjcwMCIgaGVpZ2h0PSI0NzUiIGZpbGw9IiNjYmNiY2IiIC8+CiAgPHJlY3QgaWQ9InIiIHdpZHRoPSI3MDAiIGhlaWdodD0iNDc1IiBmaWxsPSJ1cmwoI2cpIiAvPgogIDxhbmltYXRlIHhsaW5rOmhyZWY9IiNyIiBhdHRyaWJ1dGVOYW1lPSJ4IiBmcm9tPSItNzAwIiB0bz0iNzAwIiBkdXI9IjFzIiByZXBlYXRDb3VudD0iaW5kZWZpbml0ZSIgIC8+Cjwvc3ZnPg=="
          />
        </Styled.LoadingProductImage>
      </Styled.ProductTileLoadingContainer>
    );
  }

  return (
    <>
      {showAddToCartConfirmation && (
        <AddToCartConfirmationModal
          name={product.name}
          price={couponProduct?.discountPrice ?? product.displayPrice}
          imageUrl={product.images[0].thumbnail}
          onClose={() => setShowAddToCartConfirmation(false)}
        />
      )}

      <Styled.ProductTileContainer
        onClick={() => {
          if (loggedInUserType === 'invisalign') {
            router.push(`/product/${product.slug}`);
          } else {
            router.push(`/dso/product/${product.slug}`);
          }
        }}
        data-testid={'product-tile'}
      >
        <DiscountRibbon
          product={product}
          doctorPromotionList={doctorPromotionList}
          dsoPromotionList={dsoPromotionList}
          isDso={isDso}
        />
        <Styled.ImageWrapper>
          {product.images && product.images.length > 0 && (
            <Styled.ProductImage key={product.images[0].id}>
              <Image
                src={product.images[0].thumbnail}
                unoptimized={isAPACStore()}
                alt="Prod Image"
                layout="fill"
                objectFit="contain"
                sizes="25vw"
                placeholder="blur"
                blurDataURL="data:image/svg+xml;base64,Cjxzdmcgd2lkdGg9IjcwMCIgaGVpZ2h0PSI0NzUiIHZlcnNpb249IjEuMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayI+CiAgPGRlZnM+CiAgICA8bGluZWFyR3JhZGllbnQgaWQ9ImciPgogICAgICA8c3RvcCBzdG9wLWNvbG9yPSIjY2JjYmNiIiBvZmZzZXQ9IjIwJSIgLz4KICAgICAgPHN0b3Agc3RvcC1jb2xvcj0iI2Y5ZjlmOSIgb2Zmc2V0PSI1MCUiIC8+CiAgICAgIDxzdG9wIHN0b3AtY29sb3I9IiNjYmNiY2IiIG9mZnNldD0iNzAlIiAvPgogICAgPC9saW5lYXJHcmFkaWVudD4KICA8L2RlZnM+CiAgPHJlY3Qgd2lkdGg9IjcwMCIgaGVpZ2h0PSI0NzUiIGZpbGw9IiNjYmNiY2IiIC8+CiAgPHJlY3QgaWQ9InIiIHdpZHRoPSI3MDAiIGhlaWdodD0iNDc1IiBmaWxsPSJ1cmwoI2cpIiAvPgogIDxhbmltYXRlIHhsaW5rOmhyZWY9IiNyIiBhdHRyaWJ1dGVOYW1lPSJ4IiBmcm9tPSItNzAwIiB0bz0iNzAwIiBkdXI9IjFzIiByZXBlYXRDb3VudD0iaW5kZWZpbml0ZSIgIC8+Cjwvc3ZnPg=="
              />
            </Styled.ProductImage>
          )}
        </Styled.ImageWrapper>
        {isAPACStore() ? (
          isRedeemEnabled ? (
            <RedeemBadge />
          ) : (
            <Styled.RedeemSpace />
          )
        ) : (
          <></>
        )}
        {/* TODO: move this to separate component for cleaner code */}
        <Styled.ProductDetails>
          <Styled.ProductTitle>{product.name}</Styled.ProductTitle>
          {isOrderable && (
            <Styled.PriceContainer>
              <Styled.PricesWrapper>
                {!hasMultipleOffers && Boolean(couponProduct) && (
                  <Styled.DiscountedPrice>
                    {couponProduct?.discountPrice}
                  </Styled.DiscountedPrice>
                )}
                <Styled.ProductPrice
                  $isDiscount={Boolean(
                    !hasMultipleOffers && Boolean(couponProduct)
                  )}
                >
                  {product.displayPrice}
                </Styled.ProductPrice>
              </Styled.PricesWrapper>
              {isRedeemEnabled && (
                <Styled.ProductPriceWithRedeemPoint>
                  <div>{i18n['or']}</div>
                  <Styled.ProductPrice>
                    {redeemProduct?.displayPriceWithoutPoints}
                  </Styled.ProductPrice>
                </Styled.ProductPriceWithRedeemPoint>
              )}
              {isTypodont && (
                <Styled.HelpText>
                  {i18n['(For educational purpose only)']}
                </Styled.HelpText>
              )}
            </Styled.PriceContainer>
          )}
        </Styled.ProductDetails>
        <Styled.Bookmarked
          className="bookmark"
          onClick={(event: any) => {
            event.stopPropagation();
            handleToggleFavourites(product.variantId);
          }}
          isBookmarked={bookmark}
        >
          {bookmark ? (
            <Image
              src="/images/icons/favSelected.svg"
              alt="favourites"
              width="24"
              height="24"
            />
          ) : (
            <Image
              src="/images/icons/favNonSelected.svg"
              alt="favourites"
              width="24"
              height="24"
            />
          )}
        </Styled.Bookmarked>
      </Styled.ProductTileContainer>
    </>
  );
};

export default ProductTile;
